<template>
  <div>
    <div class="infoWindowHeader">
      <InfoWindowName
        v-if="localContent.title || editing"
        :info-window-name="localContent.title"
        placeholder="Building Name"
        :editing="editing"
        data-testid="infoWindowName"
        @updateName="updateName"
      />
    </div>
    <div class="infoWindowBody">
      <DisplayFields
        :display-fields="localContent.displayFields"
        :editing="editing"
        data-testid="buildingDisplayFields"
        @addDisplayField="(...args) => localContent.addDisplayField(...args)"
        @updateDisplayField="(...args) => localContent.updateDisplayField(...args)"
        @deleteDisplayField="(...args) => localContent.deleteDisplayField(...args)"
      />
    </div>
  </div>
</template>

<script>
// Components
import InfoWindowName from './sections/Header/InfoWindowName.vue';
import DisplayFields from './DisplayFields/DisplayFields.vue';

// Tools & Services
import {mapActions, mapMutations, mapState} from 'vuex';
import {getCollection} from '@/lib/filters/filters.js';
import {currentUser, updateDocument} from '@/services/FirestoreService';
import {InfoWindowContent, createCompetitiveDataCenterContent} from '@/lib/classes/InfoWindowContent';
import {gmAPI, Markers} from '@/services/MapService';
import {competitiveBuildingToJSON, duplicateComp, getAuditTrailToolbarBtn} from '@/lib/classes/InfoWindowContent/helpers/competitive';
import {NEW_BUILDING_ID} from '@/lib/symbols';

export default {
  name: 'DataCenterBuildingInfoWindow',
  components: {
    InfoWindowName,
    DisplayFields,
  },
  props: {
    infoWindowContent: {
      type: InfoWindowContent,
      required: true,
    },
  },
  emits: ['setInfoWindowToolbar'],
  data() {
    return {
      /** @type {boolean} */
      editing: false,
      /** @type {InfoWindowContent} */
      localContent: this.infoWindowContent,
      /** @type {boolean} indicates that this is a saved new building and cleanup must be done on unmount */
      newBuildingSaved: false,
    };
  },
  computed: {
    ...mapState(['compPickerMode']),
    isNew() {
      return this.localContent.id === NEW_BUILDING_ID;
    },
  },
  watch: {
    // watch localContent.id to see when the property is first saved.
    // When it is saved, set a local flag to delete the temp marker and add the property to the collection on unmount
    'localContent.id': {
      handler(newId, oldId) {
        if (newId !== NEW_BUILDING_ID && oldId === NEW_BUILDING_ID) {
          this.newBuildingSaved = true;
          // Highlight does not persist, possibly related to [this issue](https://github.com/visgl/deck.gl/issues/8936)
          Markers.setHighlight('dataCenterBuildings', newId, true);
          // when a new building is saved, the highlight and close actions need to use the newly generated id
          this.localContent.host.setHighlight = (state) => Markers.setHighlight('dataCenterBuildings', newId, state);
          this.localContent.host.closeActions = () => Markers.setDraggable('dataCenterBuildings', newId, false);
        }
      },
    },
  },
  mounted() {
    if (this.isNew) {
      this.enterEditMode();
    } else {
      this.enterViewMode();
    }
  },
  beforeUnmount() {
    if (this.isNew) {
      this.cancelNewBuilding();
    }
    if (this.newBuildingSaved) {
      this.convertNewBuilding();
    }
  },
  methods: {
    ...mapMutations(['setSnack']),
    ...mapMutations('infowindow', ['updateInfoWindowContents']),
    ...mapActions('infowindow', ['closeInfoWindowTab', 'createDataCenterBuildingTab', 'closeInfoWindow']),
    enterViewMode() {
      if (this.compPickerMode) {
        this.$emit('setInfoWindowToolbar', {toolbarOptions: []});
        this.editing = false;
        this.toggleDrag(false);
        return;
      }
      const toolbarOptions = [];
      toolbarOptions.push([getAuditTrailToolbarBtn(this.localContent.id, 'dataCenterBuildings')]);
      toolbarOptions.push([{
        icon: 'ContentCopy',
        name: 'Duplicate Comp',
        action: 'duplicateMarker',
        callback: () => this.duplicateMarker(),
      }]);
      toolbarOptions.push([{
        icon: 'Pencil',
        name: 'Edit Comp',
        action: 'enterEditMode',
        callback: () => this.enterEditMode(),
      }]);
      this.$emit('setInfoWindowToolbar', {
        toolbarOptions,
      });
      this.editing = false;
      this.toggleDrag(false);
    },
    enterEditMode() {
      const toolbarOptions = [];
      toolbarOptions.push([getAuditTrailToolbarBtn(this.localContent.id, 'dataCenterBuildings')]);
      toolbarOptions.push([{
        icon: 'ContentCopy',
        name: 'Duplicate Comp',
        action: 'duplicateMarker',
        callback: () => this.duplicateMarker(),
      }]);
      toolbarOptions.push([{
        icon: 'Trash',
        name: 'Delete Comp',
        action: 'delete',
        callback: () => {
          if (this.isNew) {
            this.cancelNewBuilding();
            return;
          }
          this.deleteDataCenter();
        },
      },
      {
        icon: 'Check',
        name: 'Finish Editing',
        action: 'enterViewMode',
        callback: () => {
          this.enterViewMode();
          if (this.newBuildingSaved) {
            this.convertNewBuilding();
          }
        },
      },
      ]);

      // Change the toolbar to the 'edit mode' toolbar
      this.$emit('setInfoWindowToolbar', {toolbarOptions});
      this.editing = true;
      this.toggleDrag(true);
    },
    async duplicateMarker() {
      const newMarkerContent = await duplicateComp(this.localContent, 'dataCenterBuildings', createCompetitiveDataCenterContent);
      this.editing = false;
      this.closeInfoWindow();
      await this.updateInfoWindowContents({
        content: newMarkerContent,
      });
    },
    toggleDrag(canDrag) {
      // if this is a new building, don't enable dragging
      if (this.isNew) return;
      // When the drag event finishes, we need to update the info window content location
      const dragEndCallback = (latLng) => {
        this.localContent.location = new gmAPI.LatLng(latLng);
      };
      // Set drag on multi family competitive properties
      Markers.setDraggable(`${this.localContent.type}s`, this.localContent.id, canDrag, dragEndCallback);
    },
    /**
     * Selects the correct update function based on the current content type
     * @param {string} name
     */
    updateName(name) {
      this.localContent.updateName(name);
    },
    /**
     * Function that handles deleting when the trash icon is pressed
     */
    async deleteDataCenter() {
      const iWTabIndex = await this.closeInfoWindowTab(this.localContent.id);
      const docId = this.localContent.id;
      const localCollectionData = getCollection('dataCenterBuildings').getItem(docId);

      getCollection('dataCenterBuildings')?.removeItems({id: docId});
      const date = new Date();
      await updateDocument('beyondMaps-datacenter', docId, {
        deleted: date,
        editedOn: {
          timestamp: date,
          name: currentUser().displayName,
          uid: currentUser().uid,
        },
      });

      this.setSnack({
        message: 'Building has been deleted',
        action: {
          text: 'Undo',
          callback: () => this.undoDelete(localCollectionData, iWTabIndex),
        },
      });
    },
    async undoDelete(restoreData, iWTabIndex) {
      try {
        const date = new Date();
        updateDocument('beyondMaps-datacenter', this.localContent.id, {
          deleted: null,
          editedOn: {
            timestamp: date,
            name: currentUser().displayName,
            uid: currentUser().uid,
          },
        });
        getCollection('dataCenterBuildings').addItems(restoreData);

        const iwContent = competitiveBuildingToJSON(this.localContent);
        iwContent.editedOn = {
          timestamp: date,
          name: currentUser().displayName,
          uid: currentUser().uid,
        };
        iwContent.geohash = this.localContent.metadata?.geohash;
        iwContent.markerData = {
          collection: 'dataCenterBuildings',
          id: this.localContent.id,
          icon: 'Yellow-Balloon',
        };

        this.createDataCenterBuildingTab({
          subject: iwContent,
          insertIndex: iWTabIndex,
        });
      } catch (err) {
        console.error('Error undoing delete:', err);
      }
    },
    /**
     * Function that cancels the creation of a new building
     */
    async cancelNewBuilding() {
      // Close the info window and remove the temporary marker
      const iWTabIndex = await this.closeInfoWindowTab(this.localContent.id);
      Markers.delete('dataCenterBuildings', this.localContent.id);

      // Create a restore function that will re-open the info window and re-create the marker for the undo snackbar
      const restoreFn = () => {
        const markerData = {
          collection: 'dataCenterBuildings',
          id: this.localContent.id,
          icon: 'Yellow-Balloon',
        };

        this.createDataCenterBuildingTab({
          subject: {...this.localContent, markerData},
          insertIndex: iWTabIndex,
        });
        const marker = Markers.create({
          type: 'dataCenterBuilding',
          collection: 'dataCenterBuildings',
          id: this.localContent.id,
          latLng: {lat: this.localContent.location.lat(), lng: this.localContent.location.lng()},
          name: this.localContent.title,
          icon: 'Yellow-Balloon',
        });
        Markers.setVisible(marker.collection, [marker.id], true);
      };
      this.setSnack({
        message: 'New building has been cancelled',
        action: {
          text: 'Undo',
          callback: restoreFn,
        },
      });
    },
    /**
     * Converts a newly saved building from a temporary marker to a permanent marker and adds it to the filters
     * collection
     */
    convertNewBuilding() {
      // if this was a new building when the window was opened, but has now been saved to the db,
      // the building should be added to the collection to ensure the marker is persisted.
      getCollection('dataCenterBuildings').removeItems({id: NEW_BUILDING_ID});
      Markers.delete('dataCenterBuildings', NEW_BUILDING_ID);
      this.newBuildingSaved = false; // reset the flag now that the building has been converted to a permanent marker
    },
  },
};
</script>

<style scoped lang="scss">
.infoWindowBody {
  max-width: 400px;
}
</style>
